import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
// Home stays eager: it's the landing route and must boot with the main bundle.
// Every other page loads its own chunk on first visit (mobile performance).
import Home from "@/pages/Home";
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const DeleteAccount = lazy(() => import("@/pages/DeleteAccount"));
const AirportsPage = lazy(() => import("@/pages/AirportsPage"));
const Travel = lazy(() => import("@/pages/Travel"));
const Admin = lazy(() => import("@/pages/Admin"));
const Venues = lazy(() => import("@/pages/Venues"));
const VenueDetail = lazy(() => import("@/pages/VenueDetail"));
const About = lazy(() => import("@/pages/About"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const ForVenuesPage = lazy(() => import("@/pages/ForVenuesPage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ForEventsPage = lazy(() => import("@/pages/ForEventsPage"));
const InvestorsPage = lazy(() => import("@/pages/InvestorsPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PressKit = lazy(() => import("@/pages/PressKit"));
const VenueKit = lazy(() => import("@/pages/VenueKit"));
const Download = lazy(() => import("@/pages/Download"));
const GetApp = lazy(() => import("@/pages/GetApp"));
const Insiders = lazy(() => import("@/pages/Insiders"));
const BringGateMate = lazy(() => import("@/pages/BringGateMate"));
const AppRedirect = lazy(() => import("@/pages/AppRedirect"));
const EventPublicPage = lazy(() => import("@/pages/EventPublic"));

// Slim branded fallback shown for the split-second while a page's code chunk
// downloads on first visit (kept dark so it doesn't flash against the site).
function PageLoader() {
  return (
    <div
      data-testid="page-loader"
      className="min-h-screen bg-[#0B0F14] flex items-center justify-center"
      role="status"
      aria-label="Loading page"
    >
      <div className="h-8 w-8 rounded-full border-2 border-[#00C6AE]/20 border-t-[#00C6AE] animate-spin" />
    </div>
  );
}
import StickyMobileCTA from "@/components/StickyMobileCTA";
import ConsentBanner from "@/components/ConsentBanner";
import ClarityLoader from "@/components/ClarityLoader";
import UtmAutoTag from "@/components/UtmAutoTag";
import DfwLocationBanner from "@/components/DfwLocationBanner";
import { WaitlistProvider } from "@/context/WaitlistContext";
import { TestFlightProvider } from "@/context/TestFlightContext";
import { Toaster } from "@/components/ui/sonner";
import { trackView } from "@/lib/api";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    trackView(pathname, "view");
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function Chrome({ children }) {
  const { pathname } = useLocation();
  const bare =
    pathname.startsWith("/admin") ||
    pathname === "/venue" ||
    pathname.startsWith("/venue/") ||
    pathname === "/venue-kit" ||
    pathname.startsWith("/app") ||
    pathname.startsWith("/e/");
  return (
    <>
      {!bare && <Navbar />}
      {children}
      {!bare && <StickyMobileCTA />}
      {!bare && <Footer />}
    </>
  );
}

// Any legacy ".html" URL permanently redirects to its clean address
// (e.g. /community-guidelines.html → /community-guidelines), preserving query
// string and hash. This gate runs BEFORE route matching so param routes like
// /venues/:slug can't swallow a ".html" slug, and `replace` keeps the .html
// address out of history. Every page already declares its clean URL as
// canonical via useSEO, so each page has one address for users and crawlers.
// NOTE: a true HTTP 301 status line can only be issued by the platform edge —
// the static SPA host answers every page URL with index.html — so this is an
// instant client-side permanent redirect. (Real .html files in public/, like
// index.html, are served statically and never reach React.)
function HtmlRedirectGate({ children }) {
  const location = useLocation();
  if (location.pathname.endsWith(".html")) {
    const clean = location.pathname.slice(0, -".html".length) || "/";
    return <Navigate to={clean + location.search + location.hash} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App grain relative">
      <BrowserRouter>
        <WaitlistProvider>
          <TestFlightProvider>
            <SmoothScroll />
            <ScrollToTop />
            <UtmAutoTag />
            <Chrome>
              <HtmlRedirectGate>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/privacy-policy" element={<LegalPage />} />
                <Route path="/terms" element={<LegalPage />} />
                <Route path="/community-guidelines" element={<LegalPage />} />
                <Route path="/child-safety" element={<LegalPage />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/airports" element={<AirportsPage />} />
                {/* Retired city landing pages — condensed into /airports (2026-10-23) */}
                <Route path="/dfw" element={<Navigate to="/airports" replace />} />
                <Route path="/deep-ellum" element={<Navigate to="/airports" replace />} />
                <Route path="/uptown" element={<Navigate to="/airports" replace />} />
                <Route path="/fort-worth" element={<Navigate to="/airports" replace />} />
                <Route path="/frisco" element={<Navigate to="/airports" replace />} />
                <Route path="/bishop-arts" element={<Navigate to="/airports" replace />} />
                <Route path="/plano" element={<Navigate to="/airports" replace />} />
                <Route path="/seattle" element={<Navigate to="/airports" replace />} />
                <Route path="/austin" element={<Navigate to="/airports" replace />} />
                <Route path="/houston" element={<Navigate to="/airports" replace />} />
                <Route path="/san-antonio" element={<Navigate to="/airports" replace />} />
                <Route path="/el-paso" element={<Navigate to="/airports" replace />} />
                <Route path="/travel" element={<Travel />} />
                <Route path="/airport-lounges" element={<Navigate to="/travel" replace />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/venues" element={<ForVenuesPage />} />
                <Route path="/events" element={<ForEventsPage />} />
                <Route path="/venues/directory" element={<Venues />} />
                <Route path="/venues/:slug" element={<VenueDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/for-venues" element={<Navigate to="/venues" replace />} />
                <Route path="/organizers" element={<Navigate to="/events" replace />} />
                <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
                {/* Oct 2026 brief: /events is the canonical business-events page;
                    /organizers retired and redirected. */}
                <Route path="/organizers" element={<Navigate to="/events" replace />} />
                <Route path="/investors" element={<InvestorsPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/support" element={<Contact />} />
                <Route path="/help" element={<Contact />} />
                <Route path="/press" element={<PressKit />} />
                {/* Legacy .html URLs (old emails / older app builds) are handled
                    by the HtmlRedirectGate above, before route matching. */}
                <Route path="/admin" element={<Admin />} />
                {/* The on-site venue portal is retired — the venue app lives at
                    app.gatemate.net; every /venue portal URL bounces there. */}
                <Route path="/venue" element={<AppRedirect />} />
                <Route path="/venue/*" element={<AppRedirect />} />
                {/* Waitlist era is over — the old invite-status page now
                    permanently redirects to the download page. */}
                <Route path="/waitlist-status" element={<Navigate to="/download" replace />} />
                <Route path="/venue-kit" element={<VenueKit />} />
                <Route path="/download" element={<Download />} />
                {/* /get — QR/print entry: store auto-detect + fallback (noindex) */}
                <Route path="/get" element={<GetApp />} />
                <Route path="/insiders" element={<Insiders />} />
                <Route path="/bring-gatemate" element={<BringGateMate />} />
                {/* The built-in Workspace is retired — the real web app lives
                    at app.gatemate.net; every /app URL bounces there. */}
                <Route path="/app/*" element={<AppRedirect />} />
                <Route path="/e/:code" element={<EventPublicPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </HtmlRedirectGate>
            </Chrome>
            <Toaster position="bottom-right" theme="dark" richColors />
            <ConsentBanner />
            <DfwLocationBanner />
            <ClarityLoader />
          </TestFlightProvider>
        </WaitlistProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
